import axios from 'axios';
import { ChangeEvent, ReactNode, useState } from 'react';
import { StripeForm } from '../../components/checkout/Stripe/StripeForm';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { Res } from '../../state/api';
import { PaymentMethodType } from '../../types';
import { Badge } from '../Badge/Badge';
import { credit_card, einvoice, Icon, mail, paypal, pdf } from '../Icon/Icon';
import { AccountInfoBox } from './AccountInfoBox';
import { useFormValidationContainer } from './FormValidatorContainer';
import { InputAlertMessage } from './InputAlertMessage';
import { InputGrid } from './InputGrid';
import { InvoiceAddressInput, InvoiceAddressModel } from './InvoiceAddressInput';
import { Options } from './Options';
import './PaymentInput.css';
import { RadioButton } from './RadioButton';
import { TextInput } from './TextInput';
import { formatOrgNumber, formatZipByLocale } from './utils';

const operators = [
	'PEPPOL',
	'Addett',
	'Bankgirocentralen',
	'Basware',
	'BGC',
	'CGI',
	'Crediflow',
	'eBuilder',
	'EDI Solutions',
	'Evenex',
	'EVRY',
	'Expert Systems',
	'Fakturaportalen',
	'Handelsbanken',
	'Highjump',
	'Husera',
	'InExchange',
	'Itella',
	'KMD',
	'Logica',
	'Logiq',
	'ob10',
	'OpusCapita',
	'Pagero',
	'Palette',
	'Posten',
	'PostNord',
	'PRI Handel',
	'Proceedo',
	'ReadSoft',
	'ScanCloud',
	'SEB',
	'Stralfors',
	'Swedbank',
	'SWEDSESS',
	'Tieto',
	'Tungsten',
	'Visma',
];

const icons = {
	pdf,
	paper: mail,
	card: credit_card,
	einvoice,
	ff: einvoice,
	paypal,
};

type PaymentSummaryProps = { model: PaymentMethodModel };

export const PaymentSummary = ({ model }: PaymentSummaryProps): JSX.Element => {
	const { locale } = useConfig();
	const t = useTranslation().form;
	const { eInvoicePaymentEnabled } = useConfig();
	const formattedOrgnr = model.orgnr ? formatOrgNumber(model.orgnr, locale) : undefined;

	const addressSummary: JSX.Element = (
		<>
			<dd className="payment-summary-company">
				<span>
					{model.company} {model.careOf ? <>c/o {model.careOf}</> : null}
				</span>
				{formattedOrgnr ? (
					<Badge size="small" color="green">
						{formattedOrgnr}
					</Badge>
				) : null}
			</dd>
			{model.ref1 ? (
				<>
					<dd>
						{t.refShort}: {model.ref1}
					</dd>
				</>
			) : null}
			<dd>{model.street}</dd>

			<dd>
				{formatZipByLocale(model.zip, locale)} {model.city}
			</dd>
		</>
	);

	const type = model.type !== '' ? model.type : 'pdf';

	return (
		<AccountInfoBox>
			<dl>
				<div className="payment-type">
					<Icon shape={icons[type]} />
					<dd>
						{t.paymentSummary[type]}
						{['pdf', 'ff'].includes(model.type) && <span className="bold">{model.invoiceEmail}</span>}
					</dd>
				</div>
				{eInvoicePaymentEnabled && model.type === 'einvoice' && (
					<>
						{model.einvoiceIdentifier && <dd>{model.einvoiceIdentifier}</dd>}
						{model.einvoiceOperator && <dd>{model.einvoiceOperator}</dd>}
					</>
				)}
				{!['card', 'paypal'].includes(model.type) && addressSummary}
			</dl>
		</AccountInfoBox>
	);
};

export type PaymentMethodModel = {
	type: PaymentMethodType;
	invoiceEmail: string;
	einvoiceIdentifier: string;
	einvoiceOperator: string;
} & InvoiceAddressModel;

const EinvoiceInput = ({ onBlur }: { onBlur?: (e: ChangeEvent) => void }): JSX.Element => {
	const t = useTranslation().form;
	const { api, store, glnPattern, eInvoicePaymentEnabled } = useConfig();
	const [peppolIds, setPeppolIds] = useState<string[]>([]);
	const form = useFormValidationContainer();
	const [isIdentifierPeppol, setIsIdentfierPeppol] = useState(form.paymentOperator.value === 'PEPPOL');
	const [einvoiceValidationErrorMessage, setEinvoiceValidationError] = useState('');

	async function checkPeppol(einvoiceIdentifier: string): Promise<boolean> {
		try {
			const res = await axios.get(api + Res.peppolCheck.uri({ id: einvoiceIdentifier }));
			return Promise.resolve(res.status === 200);
		} catch (e: unknown) {
			return Promise.reject(false);
		}
	}

	async function handleEinvoiceIdChanged(einvoiceIdentifier: string) {
		let isPeppol = false;
		let validationError = '';
		const matchesGlnPattern = einvoiceIdentifier.match(glnPattern);
		const validInput = einvoiceIdentifier.match(/^([0-9:]{0,18})$/);

		if (!validInput)
			validationError =
				einvoiceIdentifier.length > 18
					? t.paymentInput.validationErrorChars
					: t.paymentInput.validationErrorGln;

		if (matchesGlnPattern) {
			if (peppolIds.includes(einvoiceIdentifier)) {
				isPeppol = true;
			} else {
				try {
					isPeppol = await checkPeppol(einvoiceIdentifier);
					if (isPeppol) setPeppolIds([...peppolIds, einvoiceIdentifier]);
				} catch (e) {
					console.log(e);
				}
			}
		}

		setEinvoiceValidationError(validationError);
		setIsIdentfierPeppol(isPeppol);
		if (isPeppol) {
			form.paymentOperator.setValueAndValidate('PEPPOL');
		}
	}

	if (!eInvoicePaymentEnabled) return <></>;

	return (
		<InputGrid className="child-under-radio">
			{(store === 'kgse' || store === 'kgdk') && (
				<>
					<TextInput
						id="einvoice-identifier"
						placeholder={
							einvoiceValidationErrorMessage !== ''
								? einvoiceValidationErrorMessage
								: t.einvoiceIdentifier
						}
						onChange={(e) => {
							form.paymentIdentifier.handleOnChange(e);
							handleEinvoiceIdChanged(e.target.value);
						}}
						onBlur={(e) => {
							if (onBlur) onBlur(e);
							form.paymentIdentifier.validate();
						}}
						value={form.paymentIdentifier.value}
						nextElement="einvoice-operator"
						status={form.paymentIdentifier.type}
						isValid={form.paymentIdentifier.isValid}
					/>
					{form.paymentIdentifier.type !== 'success' && (
						<InputAlertMessage
							type={form.paymentIdentifier.type}
							message={form.paymentIdentifier.message}
						/>
					)}

					{store === 'kgse' && (
						<Options
							id="einvoice-operator"
							placeholder={t.einvoiceOperator}
							onChange={(e) => {
								console.log('id="einvoice-operator"', e);
								form.paymentOperator.handleOnChange(e as unknown as ChangeEvent<HTMLInputElement>);
							}}
							value={form.paymentOperator.value || ''}
							disabled={isIdentifierPeppol}
							onBlur={(e) => {
								if (onBlur) onBlur(e);
								form.paymentOperator.validate();
							}}
							nextElement="invoice-company"
							size="large"
							icon
							isValid={form.paymentOperator.isValid}
							status={form.paymentOperator.type}
						>
							<option disabled value=""></option>
							{operators.map((e) => (
								<option key={e} value={e}>
									{e}
								</option>
							))}
						</Options>
					)}
					{form.paymentOperator.type !== 'success' && (
						<InputAlertMessage type={form.paymentOperator.type} message={form.paymentOperator.message} />
					)}
				</>
			)}
			<InvoiceAddressInput onBlur={onBlur} />
		</InputGrid>
	);
};

function PaymentMethodRadio({
	value,
	children,
	currentPaymentType,
	onChange,
	onBlur,
}: {
	value: string;
	children: ReactNode;
	currentPaymentType: string;
	onChange: (e: ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
}) {
	return (
		<RadioButton
			group="payment_method"
			value={value}
			checked={currentPaymentType === value}
			onChange={onChange}
			onBlur={onBlur}
		>
			{children}
		</RadioButton>
	);
}

type PaymentInputProps = {
	withoutCardDetails?: boolean;
	onBlur?: (e: ChangeEvent) => void;
};
export const PaymentInput = ({ withoutCardDetails, onBlur }: PaymentInputProps): JSX.Element => {
	const form = useFormValidationContainer();

	// const searchParams = new URLSearchParams(location.search);
	const { eInvoicePaymentEnabled, paypalEnabled } = useConfig();
	// const showPaypal = searchParams.has('test-paypal') && paypalEnabled;
	const showPaypal = paypalEnabled;

	const t = useTranslation().form;
	const p = {
		pdf: t.checkout.pdf,
		invoice: t.checkout.invoice,
		card: t.checkout.card,
		einvoice: t.checkout.einvoice,
		prepaid: t.checkout.prepaid,
		paypal: t.checkout.paypal,
	};

	function handlePaymentMethodChange(e: ChangeEvent<HTMLInputElement>) {
		form.paymentType.setValue(e.target.value as PaymentMethodType);
	}

	return (
		<div id="payment-form-part">
			{/* pdf */}
			<PaymentMethodRadio
				onBlur={onBlur}
				onChange={handlePaymentMethodChange}
				currentPaymentType={form.paymentType.value}
				value="pdf"
			>
				<Icon shape={icons.pdf} />
				<span className="ml-2">{p.pdf}</span>
			</PaymentMethodRadio>
			{form.paymentType.value === 'pdf' && (
				<InputGrid className="child-under-radio">
					<TextInput
						id="email-for-invoice"
						autocomplete="email"
						placeholder={t.emailForInvoice}
						value={form.paymentEmail.value}
						onChange={form.paymentEmail.handleOnChange}
						onBlur={(e) => {
							if (onBlur) onBlur(e);
							form.paymentEmail.validate;
						}}
						nextElement="invoice-company"
						isValid={form.paymentEmail.isValid}
						status={form.paymentEmail.type}
					/>
					{form.paymentEmail.type !== 'success' && (
						<InputAlertMessage type={form.paymentEmail.type} message={form.paymentEmail.message} />
					)}
					<InvoiceAddressInput onBlur={onBlur} />
				</InputGrid>
			)}

			{/* paper */}
			<PaymentMethodRadio
				onBlur={onBlur}
				onChange={handlePaymentMethodChange}
				currentPaymentType={form.paymentType.value}
				value="paper"
			>
				<Icon shape={icons.paper} />
				<span className="ml-2">{p.invoice}</span>
			</PaymentMethodRadio>
			{form.paymentType.value === 'paper' && (
				<InputGrid className="child-under-radio">
					<InvoiceAddressInput onBlur={onBlur} />
				</InputGrid>
			)}

			{!withoutCardDetails && (
				<>
					{/* card */}
					<PaymentMethodRadio
						onBlur={onBlur}
						onChange={handlePaymentMethodChange}
						currentPaymentType={form.paymentType.value}
						value="card"
					>
						<Icon shape={icons.card} />
						<span className="ml-2">{p.card}</span>
					</PaymentMethodRadio>
					{form.paymentType.value === 'card' && (
						<InputGrid className="child-under-radio">
							<StripeForm />
						</InputGrid>
					)}
				</>
			)}

			{/* paypal */}
			{showPaypal && (
				// {paypalEnabled && (
				<PaymentMethodRadio
					onBlur={onBlur}
					onChange={handlePaymentMethodChange}
					currentPaymentType={form.paymentType.value}
					value="paypal"
				>
					<Icon shape={icons.paypal} />
					<span className="ml-2">{p.paypal}</span>
				</PaymentMethodRadio>
			)}

			{/* einvoice */}
			{eInvoicePaymentEnabled && (
				<PaymentMethodRadio
					onBlur={onBlur}
					onChange={handlePaymentMethodChange}
					currentPaymentType={form.paymentType.value}
					value="einvoice"
				>
					<Icon shape={icons.einvoice} />
					<span className="ml-2">{p.einvoice}</span>
				</PaymentMethodRadio>
			)}
			{eInvoicePaymentEnabled && form.paymentType.value === 'einvoice' && <EinvoiceInput onBlur={onBlur} />}

			{/* ff */}
			<PaymentMethodRadio
				onBlur={onBlur}
				onChange={handlePaymentMethodChange}
				currentPaymentType={form.paymentType.value}
				value="ff"
			>
				<Icon shape={icons.ff} />
				<span className="ml-2">{p.prepaid}</span>
			</PaymentMethodRadio>
			{form.paymentType.value === 'ff' && (
				<InputGrid className="child-under-radio">
					<TextInput
						id="prepaid-email-for-invoice"
						autocomplete="email"
						placeholder={t.emailForInvoice}
						value={form.paymentEmail.value}
						onChange={form.paymentEmail.handleOnChange}
						nextElement="invoice-company"
						isValid={form.paymentEmail.isValid}
						status={form.paymentEmail.type}
						onBlur={() => form.paymentEmail.validate()}
					/>
					{form.paymentEmail.type !== 'success' && (
						<InputAlertMessage type={form.paymentEmail.type} message={form.paymentEmail.message} />
					)}
					<InvoiceAddressInput onBlur={onBlur} />
				</InputGrid>
			)}
		</div>
	);
};
